import {
  memo,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Search, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './Dialog';
import { Button } from './Button';
import { Input } from './Input';
import { Checkbox } from './Checkbox';
import { cn } from './cn';
import type { SearchSelectOption, SearchSelectPage, SearchSelectLoader } from './SearchSelectField';

const DEFAULT_DEBOUNCE = 250;
const LOAD_MORE_THRESHOLD_PX = 80;

export interface MultiSelectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  loadOptions: SearchSelectLoader;
  resolveOption: (value: string) => Promise<SearchSelectOption | null>;
  initialValue: ReadonlyArray<string>;
  onApply: (values: ReadonlyArray<string>) => void;
  title?: string;
  description?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  loadingMessage?: string;
  loadMoreLabel?: string;
  applyLabel?: string;
  cancelLabel?: string;
  emptySelectionHint?: ReactNode;
  excludeValues?: ReadonlyArray<string>;
  debounceMs?: number;
}

const useDebounced = <T,>(value: T, delay: number): T => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    if (value === debounced) return;
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay, debounced]);
  return debounced;
};

interface PagedState {
  items: ReadonlyArray<SearchSelectOption>;
  page: number;
  hasMore: boolean;
  loading: boolean;
  error: Error | null;
}

const INITIAL: PagedState = {
  items: [],
  page: -1,
  hasMore: false,
  loading: false,
  error: null,
};

interface OptionRowProps {
  option: SearchSelectOption;
  checked: boolean;
  rowId: string;
  onToggle: (value: string, checked: boolean) => void;
}

const OptionRow = memo(({ option, checked, rowId, onToggle }: OptionRowProps) => (
  <li
    className={cn(
      'flex items-center gap-3 rounded px-3 py-2',
      'transition-colors duration-[var(--motion-fast)]',
      'hover:bg-[color:var(--surface-1)]',
      checked && 'bg-[color:var(--surface-1)]',
    )}
  >
    <Checkbox
      id={rowId}
      checked={checked}
      onCheckedChange={(c) => onToggle(option.value, c === true)}
    />
    {option.thumbnailUrl ? (
      <img
        src={option.thumbnailUrl}
        alt=""
        className="h-8 w-8 rounded object-cover"
        style={{
          background: 'var(--surface-1)',
          boxShadow: 'inset 0 0 0 1px var(--divider-strong)',
        }}
      />
    ) : null}
    <label htmlFor={rowId} className="flex flex-1 cursor-pointer flex-col">
      <span className="text-sm" style={{ color: 'var(--content-strong)' }}>
        {option.label}
      </span>
      {option.hint ? (
        <span className="text-xs" style={{ color: 'var(--content-muted)' }}>
          {option.hint}
        </span>
      ) : null}
    </label>
  </li>
));
OptionRow.displayName = 'MultiSelectModal.OptionRow';

export const MultiSelectModal = ({
  open,
  onOpenChange,
  loadOptions,
  resolveOption,
  initialValue,
  onApply,
  title = '항목 선택',
  description,
  searchPlaceholder = '검색',
  emptyMessage = '검색 결과가 없습니다',
  loadingMessage = '검색 중...',
  loadMoreLabel = '더 보기',
  applyLabel = '선택 적용',
  cancelLabel = '취소',
  emptySelectionHint,
  excludeValues,
  debounceMs = DEFAULT_DEBOUNCE,
}: MultiSelectModalProps) => {
  const [keyword, setKeyword] = useState('');
  const debounced = useDebounced(keyword, debounceMs);
  const [selected, setSelected] = useState<ReadonlyArray<string>>(initialValue);
  const [state, setState] = useState<PagedState>(INITIAL);
  const [resolvedCache, setResolvedCache] = useState<Record<string, SearchSelectOption>>({});
  const inflightRef = useRef<{ query: string; page: number } | null>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const sentinelRef = useRef<HTMLLIElement>(null);
  const listboxId = useId();

  // Reset internal selection when the modal is (re)opened.
  useEffect(() => {
    if (open) {
      setSelected(initialValue);
      setKeyword('');
    }
  }, [open, initialValue]);

  // First page load on open + query change.
  useEffect(() => {
    if (!open) return;
    let active = true;
    inflightRef.current = { query: debounced, page: 0 };
    setState({ ...INITIAL, loading: true });

    loadOptions(debounced, 0)
      .then((res) => {
        if (!active) return;
        setState({
          items: res.items,
          page: 0,
          hasMore: res.hasMore,
          loading: false,
          error: null,
        });
      })
      .catch((err: unknown) => {
        if (!active) return;
        setState({
          items: [],
          page: 0,
          hasMore: false,
          loading: false,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      })
      .finally(() => {
        if (active && inflightRef.current?.query === debounced) {
          inflightRef.current = null;
        }
      });

    return () => {
      active = false;
    };
  }, [open, debounced, loadOptions]);

  // Resolve any pre-selected value that the current page does not yet contain
  // (for "선택 N건" chip rendering).
  useEffect(() => {
    if (!open) return;
    const missing = selected.filter(
      (v) => !resolvedCache[v] && !state.items.some((it) => it.value === v),
    );
    if (missing.length === 0) return;
    let active = true;
    Promise.all(
      missing.map(async (v) => {
        const r = await resolveOption(v).catch(() => null);
        return [v, r ?? { value: v, label: v }] as const;
      }),
    ).then((pairs) => {
      if (!active) return;
      setResolvedCache((prev) => {
        const next = { ...prev };
        pairs.forEach(([k, opt]) => {
          next[k] = opt;
        });
        return next;
      });
    });
    return () => {
      active = false;
    };
  }, [open, selected, resolveOption, state.items, resolvedCache]);

  const loadMore = useCallback(() => {
    setState((prev) => {
      if (prev.loading || !prev.hasMore || prev.page < 0) return prev;
      const nextPage = prev.page + 1;
      if (inflightRef.current?.page === nextPage) return prev;
      inflightRef.current = { query: debounced, page: nextPage };

      loadOptions(debounced, nextPage)
        .then((res: SearchSelectPage) => {
          setState((curr) => {
            if (inflightRef.current?.query !== debounced) return curr;
            const seen = new Set(curr.items.map((it) => it.value));
            const merged = [
              ...curr.items,
              ...res.items.filter((it) => !seen.has(it.value)),
            ];
            return {
              items: merged,
              page: nextPage,
              hasMore: res.hasMore,
              loading: false,
              error: null,
            };
          });
        })
        .catch((err: unknown) => {
          setState((curr) => ({
            ...curr,
            loading: false,
            error: err instanceof Error ? err : new Error(String(err)),
          }));
        })
        .finally(() => {
          if (inflightRef.current?.page === nextPage) {
            inflightRef.current = null;
          }
        });

      return { ...prev, loading: true };
    });
  }, [debounced, loadOptions]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = listRef.current;
    if (!sentinel || !root || !state.hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      {
        root,
        rootMargin: `0px 0px ${LOAD_MORE_THRESHOLD_PX}px 0px`,
        threshold: 0.01,
      },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [state.hasMore, state.items.length, loadMore]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const excludeSet = useMemo(() => new Set(excludeValues ?? []), [excludeValues]);
  const visibleItems = useMemo(
    () => state.items.filter((opt) => !excludeSet.has(opt.value)),
    [state.items, excludeSet],
  );

  const toggle = useCallback((value: string, checked: boolean) => {
    setSelected((prev) =>
      checked ? (prev.includes(value) ? prev : [...prev, value]) : prev.filter((v) => v !== value),
    );
  }, []);

  const removeSelected = useCallback((value: string) => {
    setSelected((prev) => prev.filter((v) => v !== value));
  }, []);

  const clearAll = useCallback(() => setSelected([]), []);

  const apply = useCallback(() => {
    onApply(selected);
    onOpenChange(false);
  }, [selected, onApply, onOpenChange]);

  const cancel = useCallback(() => onOpenChange(false), [onOpenChange]);

  const selectedChips = useMemo(
    () =>
      selected.map((v) => {
        const fromList = state.items.find((it) => it.value === v);
        return fromList ?? resolvedCache[v] ?? { value: v, label: v };
      }),
    [selected, state.items, resolvedCache],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>

        <div className="mt-4 flex flex-col gap-3">
          <div className="relative">
            <Search
              size={14}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
              style={{ color: 'var(--content-muted)' }}
            />
            <Input
              autoFocus
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder={searchPlaceholder}
              className="pl-8"
              aria-label={searchPlaceholder}
            />
          </div>

          <div
            className="rounded-md border"
            style={{ borderColor: 'var(--divider-strong)', background: 'var(--surface-0)' }}
          >
            <ul
              ref={listRef}
              role="listbox"
              aria-multiselectable
              id={listboxId}
              className="max-h-[320px] overflow-y-auto p-1"
            >
              {state.loading && state.items.length === 0 ? (
                <li className="px-3 py-6 text-center text-xs" style={{ color: 'var(--content-muted)' }}>
                  {loadingMessage}
                </li>
              ) : null}

              {state.error ? (
                <li className="px-3 py-6 text-center text-xs" style={{ color: 'var(--signal-danger)' }}>
                  {state.error.message}
                </li>
              ) : null}

              {!state.loading && !state.error && visibleItems.length === 0 ? (
                <li className="px-3 py-6 text-center text-xs" style={{ color: 'var(--content-muted)' }}>
                  {emptyMessage}
                </li>
              ) : null}

              {visibleItems.map((opt, idx) => (
                <OptionRow
                  key={opt.value}
                  option={opt}
                  checked={selectedSet.has(opt.value)}
                  rowId={`${listboxId}-row-${idx}`}
                  onToggle={toggle}
                />
              ))}

              {state.hasMore ? (
                <li ref={sentinelRef} className="px-3 py-2">
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={loadMore}
                    disabled={state.loading}
                    className={cn(
                      'w-full rounded border border-dashed px-2 py-1.5 text-center text-xs',
                      'transition-colors duration-[var(--motion-fast)]',
                      'hover:border-[color:var(--accent-default)] hover:text-[color:var(--accent-default)]',
                      'disabled:cursor-not-allowed disabled:opacity-60',
                    )}
                    style={{
                      color: 'var(--content-muted)',
                      borderColor: 'var(--divider-strong)',
                    }}
                  >
                    {state.loading ? '추가 로드 중...' : loadMoreLabel}
                  </button>
                </li>
              ) : null}
            </ul>
          </div>

          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              {selected.length === 0 && emptySelectionHint ? (
                <p
                  className="rounded p-2 text-xs"
                  style={{
                    background: 'var(--surface-0)',
                    border: '1px dashed var(--divider-strong)',
                    color: 'var(--content-muted)',
                  }}
                >
                  {emptySelectionHint}
                </p>
              ) : (
                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: 'var(--content-muted)' }}>
                      선택 {selected.length}건
                    </span>
                    {selected.length > 0 ? (
                      <button
                        type="button"
                        onClick={clearAll}
                        className="text-xs underline"
                        style={{ color: 'var(--content-muted)' }}
                      >
                        전체 해제
                      </button>
                    ) : null}
                  </div>
                  {selected.length > 0 ? (
                    <div className="flex max-h-20 flex-wrap gap-1.5 overflow-y-auto">
                      {selectedChips.map((opt) => (
                        <span
                          key={opt.value}
                          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs"
                          style={{
                            background: 'var(--surface-1)',
                            border: '1px solid var(--divider-strong)',
                            color: 'var(--content-strong)',
                          }}
                        >
                          <span className="max-w-[160px] truncate">{opt.label}</span>
                          <button
                            type="button"
                            onClick={() => removeSelected(opt.value)}
                            aria-label={`${opt.label} 제거`}
                            className={cn(
                              'rounded-full p-0.5 transition-transform duration-[var(--motion-fast)]',
                              'hover:scale-110 hover:text-[color:var(--accent-default)]',
                            )}
                            style={{ color: 'var(--content-muted)' }}
                          >
                            <X size={10} />
                          </button>
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t pt-3" style={{ borderColor: 'var(--divider-strong)' }}>
            <Button type="button" variant="ghost" onClick={cancel}>
              {cancelLabel}
            </Button>
            <Button type="button" variant="primary" onClick={apply}>
              {applyLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
